import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const VideoMarketing = () => {
  const testimonials = [
    {
      id: 1,
      name: "Akash Verma",
      review:
        "This Video Marketing Expert course was incredible! I learned the entire process from scriptwriting and shooting to editing and distribution across platforms like YouTube and TikTok. My brand's video content now gets amazing engagement!",
      rating: 5,
      date: "2024-06-08",
      profileImg: "https://randomuser.me/api/portraits/men/33.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Deepika Singh",
      review:
        "The practical sessions on video editing software and YouTube SEO were invaluable. I can now create professional-quality videos and optimize them for maximum reach. This training in Noida is truly comprehensive.",
      rating: 5,
      date: "2024-06-05",
      profileImg: "https://randomuser.me/api/portraits/women/43.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Rahul Kumar",
      review:
        "I was new to video production, but this course made it accessible. From understanding different video formats to running effective video ad campaigns, every aspect was covered with clarity. Highly recommend for aspiring video marketers.",
      rating: 4,
      date: "2024-06-02",
      profileImg: "https://randomuser.me/api/portraits/men/53.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Sanya Arora",
      review:
        "The Video Marketing course content is highly relevant, focusing on current trends like short-form video and live streaming. I appreciated the emphasis on measuring video performance and ROI. Excellent value and strong placement support.",
      rating: 5,
      date: "2024-05-30",
      profileImg: "https://randomuser.me/api/portraits/women/63.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Gaurav Saini",
      review:
        "This training equipped me with the skills to confidently create and manage video marketing strategies for various businesses. The hands-on projects were particularly effective in building my portfolio and confidence.",
      rating: 4,
      date: "2024-05-27",
      profileImg: "https://randomuser.me/api/portraits/men/73.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  

  const faqContent = [
    {
      title: "What is Video Marketing and why is it crucial today?",
      answer:
        "Video marketing uses video content to promote or market your product or service, increase engagement on digital and social channels, educate consumers, and reach your audience with a new medium. It's crucial because video dominates online consumption, offers higher engagement rates, builds trust, and significantly boosts conversion rates.",
    },
    {
      title: "What are the prerequisites for this Video Marketing Expert course?",
      answer:
        "No specific technical prerequisites are required. A basic familiarity with using smartphones or cameras is a plus. This course is designed for aspiring digital marketers, content creators, YouTubers, entrepreneurs, and anyone looking to leverage the power of video for business growth.",
    },
    {
      title: "What types of video content and platforms will I master?",
      answer:
        "You will learn about various video formats including **explainer videos, product videos, testimonials, live streams, short-form videos (Reels, Shorts, TikToks), and long-form content**. You'll master platforms like **YouTube, Instagram, Facebook, TikTok, LinkedIn**, and learn strategies for multi-platform distribution.",
    },
    {
      title: "Is there a high demand for Video Marketing professionals?",
      answer:
        "Absolutely. Video content is the most consumed format online, leading to an explosive demand for skilled video marketers who can strategize, produce, optimize, and analyze video campaigns. Businesses across all industries are investing heavily in video.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Yes, we provide dedicated placement assistance for our Video Marketing graduates. This includes professional resume building, mock interview preparation, portfolio reviews, and connecting you with leading digital marketing agencies, production houses, and companies in Noida and across India for roles like Video Marketing Specialist, Content Creator, and Digital Marketing Executive.",
    },
    {
      title: "What kind of practical projects will I work on?",
      answer:
        "You will work on hands-on projects such as developing a video marketing strategy, scriptwriting, basic video shooting and editing, creating engaging short-form videos, optimizing videos for YouTube SEO, setting up video ad campaigns, and analyzing video performance metrics. You'll build a strong video portfolio.",
    },
    {
      title: "What are the career opportunities after this Video Marketing training?",
      answer:
        "Upon successful completion, you can pursue exciting careers as a Video Marketing Specialist, Video Content Creator, YouTube Manager, Social Media Video Strategist, Digital Marketing Executive (with video expertise), or a freelance Video Marketing Consultant.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace (intensive fast-track vs. regular batch). We offer both immersive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your learning preferences.",
    },
  ];
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Video Marketing & Strategy",
      description: "Understanding the power of video, setting video marketing goals, identifying target audiences, and developing a holistic video strategy.",
    },
    {
      title: "Module 2: Video Content Planning & Scriptwriting",
      description: "Brainstorming video ideas, defining video types (explainer, demo, testimonial), scriptwriting for different formats, and storyboarding.",
    },
    {
      title: "Module 3: Basic Video Production (Shooting & Setup)",
      description: "Understanding camera basics (smartphone/DSLR), lighting, audio recording, background setup, and on-screen presentation tips.",
    },
    {
      title: "Module 4: Video Editing Fundamentals",
      description: "Introduction to user-friendly video editing software (e.g., DaVinci Resolve Free, CapCut, basic Premiere Pro concepts), cutting, transitions, text overlays, and music.",
    },
    {
      title: "Module 5: YouTube Marketing & SEO",
      description: "Optimizing YouTube channels, video SEO (titles, descriptions, tags, thumbnails), playlists, community features, and analytics.",
    },
    {
      title: "Module 6: Short-Form Video Mastery (Reels, Shorts, TikTok)",
      description: "Strategies for creating engaging short-form content, trending audio, hooks, vertical video best practices, and platform-specific algorithms.",
    },
    {
      title: "Module 7: Video Advertising & Promotion",
      description: "Setting up video ad campaigns on YouTube Ads (Google Ads), Facebook/Instagram Video Ads, and understanding video ad formats.",
    },
    {
      title: "Module 8: Video Performance Analytics & ROI",
      description: "Key video metrics (watch time, engagement rate, completion rate), YouTube Analytics, Facebook Video Insights, and measuring video marketing ROI.",
    },
    {
      title: "Module 9: Live Streaming Strategy & Execution",
      description: "Planning live sessions, choosing platforms (YouTube Live, Facebook Live, Instagram Live), engagement tactics, and post-live repurposing.",
    },
    {
      title: "Module 10: Advanced Video Marketing Concepts",
      description: "Interactive video, personalized video, repurposing content, user-generated video (UGV), and influencer collaborations through video.",
    },
    {
      title: "Module 11: Video Content Distribution & Amplification",
      description: "Strategies for distributing video content across websites, social media, email, and paid channels to maximize reach.",
    },
    {
      title: "Module 12: Live Video Marketing Project & Portfolio Building",
      description: "Develop and execute a complete video marketing campaign for a real or mock business, including content creation, optimization, promotion, and performance analysis.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Video Marketing Expert Course in Noida | YouTube, Reels, TikTok Training</title>
        <meta
          name="description"
          content="Become a certified Video Marketing Expert in Noida. Master video strategy, production, editing, YouTube SEO, short-form video, and paid video ads with placement support."
        />
        <meta
          name="keywords"
          content="Video Marketing expert course Noida, YouTube Marketing training, Reels marketing, TikTok marketing, Video production, Video SEO, Video Advertising, Digital Marketing, Video Content Creation, Video Marketing certification"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://i0.wp.com/shopnix.io/blog/wp-content/uploads/2017/01/Video-Marketing-for-E-commerce-Business.jpg?fit=3000%2C1611&ssl=1"
          alt="Video Marketing Expert Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Become a <span className="text-orange-400">Video Marketing Expert</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master video strategy, production, and distribution across top platforms to captivate audiences and drive results.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-orange-700 hover:bg-orange-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
               <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Video Marketing Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn Video Marketing Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Master Video Marketing?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Video content is dominating the internet, offering unparalleled engagement and conversion potential. As a video marketing expert, you'll be at the forefront of creating compelling visual stories that connect with audiences and drive business growth across all digital channels.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Unmatched Engagement</h3>
              <p className="text-gray-600">Capture attention and connect deeply with your target audience.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Explosive Growth Market</h3>
              <p className="text-gray-600">High demand for video professionals across all industries.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Versatile & High ROI</h3>
              <p className="text-gray-600">Use video across all marketing funnels for maximum impact.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🎬', title: 'End-to-End Video Creation', desc: 'From scriptwriting & shooting to editing & post-production.' },
              { icon: '🚀', title: 'YouTube & Short-Form Video SEO', desc: 'Optimize videos for discoverability on YouTube, Reels, TikTok.' },
              { icon: '💸', title: 'Paid Video Advertising', desc: 'Run impactful video ad campaigns on Google Ads, Meta Ads, etc.' },
              { icon: '👨‍🏫', title: 'Industry Expert Trainers', desc: 'Learn cutting-edge strategies from experienced video marketers.' },
              { icon: '🎓', title: 'Certification & Portfolio', desc: 'Receive a professional certificate and build a strong video portfolio.' },
              { icon: '🤝', title: '100% Placement Assistance', desc: 'Dedicated support for career opportunities in video marketing.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-red-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-red-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Platforms You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Video Marketing Expert training provides extensive hands-on experience with the leading video platforms and content creation tools.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'YouTube', logo: 'https://pngdownload.io/wp-content/uploads/2023/12/YouTube-Logo-PNG-Symbol-for-Video-Platform-Transparent-jpg.webp', desc: 'Video hosting & marketing' },
              { name: 'Instagram Reels', logo: 'https://download.logo.wine/logo/Instagram/Instagram-Logo.wine.png', desc: 'Short-form video content' },
              { name: 'Facebook Video', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/facebook/facebook-original.svg', desc: 'Social video sharing' },
              { name: 'CapCut / InShot', logo: 'https://static.vecteezy.com/system/resources/previews/055/030/388/non_2x/capcut-circle-icon-logo-symbol-free-png.png', desc: 'Mobile video editing' },
              { name: 'Canva', logo: 'https://freelogopng.com/images/all_img/1656734858canva-png-logo.png', desc: 'Thumbnail & graphic design' },
              { name: 'Meta Ads Manager', logo: 'https://www.webkeyindia.com/uploaded_files/meta-ads-agency.png', desc: 'Facebook & Instagram ads' },
              { name: 'AI Video Tools (concepts)', logo: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/e6/82/32/e682327a-82b2-b22e-1495-786d66321538/AppIcon-1x_U007emarketing-0-6-0-85-220-0.png/1200x630wa.png', desc: 'AI-powered content creation' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2 object-contain" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
                {tool.desc && <p className="text-gray-500 text-xs mt-1">{tool.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-red-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities in Video Marketing</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A certification as a **Video Marketing Expert** opens doors to dynamic roles where you'll create visual narratives that engage audiences and drive results.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '🎥', title: 'Video Marketing Specialist', desc: 'Develop, execute, and manage video marketing strategies across platforms.' },
              { icon: '✍️', title: 'Video Content Creator', desc: 'Produce engaging video content, from script to final cut, for various needs.' },
              { icon: '▶️', title: 'YouTube Channel Manager', desc: 'Grow and optimize YouTube channels for audience engagement and monetization.' },
              { icon: '📱', title: 'Social Media Video Strategist', desc: 'Focus on short-form video content for platforms like TikTok and Reels.' },
              { icon: '📈', title: 'Digital Marketing Executive (Video)', desc: 'Oversee digital channels with a strong specialization in video content.' },
              { icon: '💡', title: 'Freelance Video Marketing Consultant', desc: 'Offer expert video marketing services to diverse businesses and brands.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-red-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-blue-300 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Video Marketing Expert training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Video Marketing Expert Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name (Optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-red-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-red-900">
            What Our Video Marketing Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-red-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-red-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-red-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default VideoMarketing;